import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Eraser, PenTool, Type, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getPublicQuoteStrings, normalizePublicQuoteLocale, type PublicQuoteLocale } from '@/lib/publicQuoteLocale';

interface SignatureCanvasProps {
  width?: number;
  height?: number;
  onSignatureChange?: (dataUrl: string | null) => void;
  disabled?: boolean;
  existingSignature?: string | null;
  /** Publieke offerte: taal voor knoppen/labels (default nl) */
  locale?: PublicQuoteLocale;
}

type Mode = 'draw' | 'type';

const SIGNATURE_FONTS = [
  { name: 'Dancing Script', css: "'Dancing Script', cursive" },
  { name: 'Great Vibes', css: "'Great Vibes', cursive" },
  { name: 'Caveat', css: "'Caveat', cursive" },
  { name: 'Satisfy', css: "'Satisfy', cursive" },
];

function SignatureCanvas({
  width = 500,
  height = 160,
  onSignatureChange,
  disabled = false,
  existingSignature,
  locale,
}: SignatureCanvasProps) {
  const copy = useMemo(
    () => getPublicQuoteStrings(normalizePublicQuoteLocale(locale)).signatureCanvas,
    [locale],
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [mode, setMode] = useState<Mode>('draw');
  const [typedName, setTypedName] = useState('');
  const [selectedFont, setSelectedFont] = useState(0);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const historyRef = useRef<ImageData[]>([]);
  const [historyCount, setHistoryCount] = useState(0);

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Caveat:wght@600&family=Satisfy&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    link.onload = () => setFontsLoaded(true);
    return () => { document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    if (existingSignature && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;
      const img = new window.Image();
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = existingSignature;
    } else if (!existingSignature && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, width, height);
        setHasSignature(false);
      }
    }
  }, [existingSignature, width, height]);

  const saveToHistory = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    historyRef.current.push(ctx.getImageData(0, 0, width, height));
    setHistoryCount(historyRef.current.length);
  }, [width, height]);

  const getCanvasPoint = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const touch = e.touches[0];
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (disabled || mode !== 'draw') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    saveToHistory();
    const { x, y } = getCanvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, [disabled, mode, getCanvasPoint, saveToHistory]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled || mode !== 'draw') return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasPoint(e);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing, disabled, mode, getCanvasPoint]);

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setHasSignature(true);
    const dataUrl = canvasRef.current?.toDataURL('image/png') || null;
    onSignatureChange?.(dataUrl);
  }, [isDrawing, onSignatureChange]);

  const handleUndo = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || historyRef.current.length === 0) return;
    const prev = historyRef.current.pop()!;
    setHistoryCount(historyRef.current.length);
    ctx.putImageData(prev, 0, 0);
    if (historyRef.current.length === 0) {
      setHasSignature(false);
      onSignatureChange?.(null);
    } else {
      const dataUrl = canvasRef.current?.toDataURL('image/png') || null;
      onSignatureChange?.(dataUrl);
    }
  }, [onSignatureChange]);

  const renderTypedSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, width, height);
    if (!typedName.trim()) {
      setHasSignature(false);
      onSignatureChange?.(null);
      return;
    }
    const font = SIGNATURE_FONTS[selectedFont];
    ctx.font = `48px ${font.css}`;
    ctx.fillStyle = '#1a1a2e';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName, width / 2, height / 2);
    setHasSignature(true);
    onSignatureChange?.(canvas.toDataURL('image/png'));
  }, [typedName, selectedFont, width, height, onSignatureChange]);

  useEffect(() => {
    if (mode === 'type' && fontsLoaded) {
      renderTypedSignature();
    }
  }, [mode, typedName, selectedFont, fontsLoaded, renderTypedSignature]);

  const handleClear = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    setHasSignature(false);
    setTypedName('');
    historyRef.current = [];
    setHistoryCount(0);
    onSignatureChange?.(null);
  }, [width, height, onSignatureChange]);

  const handleModeSwitch = useCallback((newMode: Mode) => {
    handleClear();
    setMode(newMode);
  }, [handleClear]);

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-2">
        <Button
          type="button"
          variant={mode === 'draw' ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => handleModeSwitch('draw')}
          disabled={disabled}
        >
          <PenTool className="h-3 w-3" />
          {copy.draw}
        </Button>
        <Button
          type="button"
          variant={mode === 'type' ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => handleModeSwitch('type')}
          disabled={disabled}
        >
          <Type className="h-3 w-3" />
          {copy.type}
        </Button>
        <div className="flex items-center gap-1 ml-auto">
          {mode === 'draw' && historyCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={handleUndo}
              disabled={disabled}
            >
              <Undo2 className="h-3 w-3" />
              {copy.undo}
            </Button>
          )}
          {hasSignature && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1 text-destructive hover:text-destructive"
              onClick={handleClear}
              disabled={disabled}
            >
              <Eraser className="h-3 w-3" />
              {copy.clear}
            </Button>
          )}
        </div>
      </div>

      {mode === 'type' && (
        <div className="mb-2 space-y-2">
          <Input
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={copy.namePlaceholder}
            className="h-8 text-sm"
            disabled={disabled}
          />
          <div className="flex gap-1">
            {SIGNATURE_FONTS.map((font, idx) => (
              <button
                key={font.name}
                type="button"
                className={`px-2 py-1 text-sm rounded border transition-colors ${idx === selectedFont ? 'border-primary bg-primary/10' : 'border-gray-200 hover:border-gray-300'}`}
                style={{ fontFamily: font.css }}
                onClick={() => setSelectedFont(idx)}
                disabled={disabled}
              >
                Aa
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className="relative rounded-md bg-white"
        style={{
          border: `2px ${hasSignature ? 'solid #16a34a' : 'dashed #d1d5db'}`,
          cursor: mode === 'draw' && !disabled ? 'crosshair' : 'default',
        }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="w-full"
          style={{ touchAction: 'none', display: 'block' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && mode === 'draw' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-gray-400">{copy.drawHint}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignatureCanvas;
